import React, { useEffect, useRef, useState } from 'react';
import {
  CafCorpusAction, CafCorrectionGuidanceRequest, CafCorrectionPreviewResponse, CafCorrectionResult, CafDecisionPrecedent,
  CafDecisionReviewPage, CafDecisionRow, CafFinalResponseStance, CafTaskCorrection, FromTaskPayload, confirmCafCorrection, getCafCorpusEntry,
  previewCafCorrection, recordCafDecisionReview, recordCorpusFromTask,
} from '../../../api/cafCorpus';
import { FIRST_LABEL_REASON, actionCopy } from '../copy';
import { FinalResponseSelect } from '../FinalResponseSelect';
import { useFinalResponseSupported } from '../finalResponseSupport';
import { formatClock } from '../format';
import { senderPartition } from '../lenders';
import { Modal } from '../Overlay';
import {
  MSG_NOT_CONFIRMED, MSG_TRY_AGAIN, classifyFailure, finishPublication, newIdempotencyKey, runSave,
} from '../saveEngine';
import { useToast } from '../ToastContext';
import { ConflictWarnings } from '../teach/ConflictWarnings';

type Outcome = 'done' | 'attach' | 'archive' | 'staff';

const OUTCOMES: Array<{ key: Outcome; label: string; action: CafCorpusAction; claim: boolean }> = [
  { key: 'done', label: 'Attach to a claim and mark done', action: 'AUTO_ASSIGN_COMPLETE', claim: true },
  { key: 'attach', label: 'Attach to a claim, leave it open', action: 'AUTO_ASSIGN_ONLY', claim: true },
  { key: 'archive', label: 'Archive it', action: 'AUTO_ARCHIVE', claim: false },
  // Ryan's "Mark as manual" ruling (Q6): one term across both tabs. The key stays 'staff'.
  { key: 'staff', label: 'Mark as manual', action: 'MANUAL_REVIEW', claim: false },
];

const STEPS: Array<['repair' | 'guidance' | 'outbound', string]> = [
  ['repair', 'the fix to this email'], ['guidance', 'the approved example'], ['outbound', 'the email to the client'],
];

const outstanding = (status: unknown) => status === 'pending' || status === 'unknown';
const settled = (result: CafCorrectionResult) => !STEPS.some(([key]) => outstanding(result[key]?.status));
const waitMs = (ms: number) => new Promise<void>((resolve) => { window.setTimeout(resolve, ms); });

/**
 * Replaying the same preview and key returns the recorded run, so this only
 * ever reads progress -- it never repeats a real effect.
 *
 * Fix round 1 (Important #2): the first call can return a committed, still-
 * finishing run (e.g. a 202-style "repair pending"). Once that has happened,
 * a LATER poll's own failure (a dropped reply, a transient race) must never
 * be reported as if the whole fix failed or expired -- it proves nothing
 * about the run that already started. So only the first call's own failure
 * is allowed to propagate; every later poll's failure just stops the loop
 * and hands back the last known (still unsettled) result.
 */
export async function confirmUntilSettled(
  taskId: string,
  payload: { preview_id: string; idempotency_key: string; confirm_outbound: boolean },
  sleep: (ms: number) => Promise<void> = waitMs,
): Promise<CafCorrectionResult> {
  let result = await confirmCafCorrection(taskId, payload);
  for (const ms of [1000, 2000, 4000]) {
    if (settled(result)) break;
    await sleep(ms);
    try {
      result = await confirmCafCorrection(taskId, payload);
    } catch {
      break;
    }
  }
  return result;
}

export type CorrectionOutcome = { state: 'done' } | { state: 'unsettled' } | { state: 'partial'; message: string };

export const describeCorrection = (result: CafCorrectionResult | null): CorrectionOutcome => {
  // Never report an unknown result as success -- a null/missing result is
  // exactly the "we don't actually know" case, same as an unsettled one.
  if (!result || !settled(result)) return { state: 'unsettled' };
  const failed = STEPS.filter(([key]) => result[key]?.status === 'failed').map(([, label]) => label);
  if (!failed.length) return { state: 'done' };
  const saved = STEPS.filter(([key]) => result[key]?.status === 'complete').map(([, label]) => label);
  return {
    state: 'partial',
    message: saved.length
      ? `Only part of this saved: ${saved.join(' and ')} saved, but ${failed.join(' and ')} didn't.`
      : `Couldn't save ${failed.join(' and ')}. Nothing else changed.`,
  };
};

const CHANGE_LABELS: Record<string, { label: string; show: (value: unknown) => string }> = {
  ai_action: { label: 'What happens', show: (value) => actionCopy(typeof value === 'string' ? value : null).label },
  assignedClaimId: { label: 'Claim', show: (value) => (typeof value === 'string' && value ? value : 'None') },
  has_final_response: { label: 'Final response', show: (value) => (value ? 'Yes' : 'No') },
  status: { label: 'Task', show: (value) => ({ completed: 'Done', in_progress: 'Open', pending: 'Waiting' } as Record<string, string>)[String(value)] ?? 'Changed' },
  storage: { label: 'Where it is', show: (value) => (value === 'archived' ? 'Archived' : 'In the lane') },
  ai_category: { label: 'Classification', show: (value) => (typeof value === 'string' && value ? value : 'None') },
};

const statusOf = (err: unknown) => (err as { response?: { status?: number } })?.response?.status;

/** The preview's own 404 for a claim reference with no claim (caf_correction_service). */
const isClaimNotFound = (err: unknown) => {
  const response = (err as { response?: { status?: number; data?: { error?: unknown } } })?.response;
  return response?.status === 404 && response.data?.error === 'claim_not_found';
};

interface Props {
  row: CafDecisionRow;
  decisionId: string;
  reviews: CafDecisionReviewPage;
  precedent: CafDecisionPrecedent | null;
  onClose: () => void;
  onFixed: () => void;
}

/**
 * Task 21: staff correct one AI decision here. The backend correction flow is
 * unchanged -- a current Wrong review, a server-held preview (harmless,
 * expires), then a confirm with an idempotency key -- this window just asks
 * four plain questions and hides all of that.
 *
 * Client-email safety (dispatch notes): the recipient shown on the check
 * screen comes ONLY from the server preview's `outbound_messages`, never
 * derived here. Confirm stays disabled until the tick is ticked, and
 * `confirm_outbound` is only ever true when both are so.
 *
 * Fix round 1 (Important #1): `caf_review_service.record_review` keys
 * idempotency on (actor, key) and rejects a reused key whose content differs
 * (`idempotency_key_reused_with_different_content`), and it compares
 * `expected_revision` against THIS decision's own latest revision, not the
 * task-wide one. So a review recorded by an earlier Check in this same modal
 * session is tracked in `lastRecorded`, keyed by its exact correction text:
 * an unchanged re-check reuses it outright (no new write, and a lost-reply
 * replay keeps the same key); a changed one mints a fresh key and supersedes
 * it at the revision this modal itself last saw -- never the task-wide
 * `current_revision`, since the server checks per decision
 * (`caf_review_service.py` `record_review`'s `current_revision` lookup is
 * scoped to `decision_id`).
 */
export const FixEmailModal: React.FC<Props> = ({ row, decisionId, reviews, precedent, onClose, onFixed }) => {
  const toast = useToast();
  const [outcome, setOutcome] = useState<Outcome>('done');
  const [claim, setClaim] = useState(row.assigned_claim_id ?? row.claim_reference ?? '');
  const [finalResponse, setFinalResponse] = useState(false);
  const [teach, setTeach] = useState(false);
  const [teachStance, setTeachStance] = useState<CafFinalResponseStance | ''>('');
  // Final review C1: an older backend refuses a correction preview whose example patch carries
  // the final-response setting, so there it is neither asked for nor sent.
  const askStance = useFinalResponseSupported();
  const stancePatch = askStance && teachStance ? { final_response_stance: teachStance } : {};
  const [teachWhy, setTeachWhy] = useState('');
  const [preview, setPreview] = useState<CafCorrectionPreviewResponse | null>(null);
  const [teachPlan, setTeachPlan] = useState<{ expected: number; token?: string } | null>(null);
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState('');
  // Final wave item 12: the claim typed (or filled in from the email) doesn't exist.
  const [claimMissing, setClaimMissing] = useState(false);
  const claimRef = useRef<HTMLInputElement | null>(null);
  // Fix round 1 (Important #2): set only when the settling loop gave up after
  // a run had already started. Terminal for this modal session -- Close is
  // the only way out, since Check/Confirm are no longer safe to offer.
  const [settleGaveUp, setSettleGaveUp] = useState(false);
  const lastRecorded = useRef<{ reviewId: string; revision: number; text: string } | null>(null);
  // Fix round 2 (Minor #2): a review write that ended MSG_NOT_CONFIRMED (the
  // original write's own reply lost, and its own replay also unconfirmed)
  // remembers its key + expected_revision here, keyed by the exact text a
  // retry would send -- so retrying the SAME text replays idempotently
  // instead of minting a fresh key against a possibly-already-moved
  // revision. A changed text still mints a fresh one.
  const pendingWrite = useRef<{ text: string; key: string; expectedRevision: number } | null>(null);
  const confirmKey = useRef('');
  const outcomeRef = useRef<HTMLSelectElement | null>(null);
  const emailLeadRef = useRef<HTMLElement | null>(null);
  const planLeadRef = useRef<HTMLParagraphElement | null>(null);
  const notConfirmedRef = useRef<HTMLDivElement | null>(null);
  const skipFocusEffect = useRef(true);

  const choice = OUTCOMES.find((item) => item.key === outcome) ?? OUTCOMES[0];
  const nothingToDo = Boolean(row.archived) && outcome === 'archive';
  const teachExample = teach && precedent !== null && Boolean(precedent.sender) && !precedent.missing;
  const teachEmail = teach && !teachExample;
  const teachBlocked = teachEmail && outcome === 'archive';
  const precedentId = precedent?.id;
  const precedentSender = precedent?.sender;
  // Updating an existing example starts from the setting it already stores, so staff only change it on purpose.
  useEffect(() => {
    if (!teachExample || !precedentId || !precedentSender) return undefined;
    let alive = true;
    getCafCorpusEntry(precedentId, precedentSender).then((live) => {
      const stored = live.final_response_stance;
      if (alive && stored) setTeachStance((current) => current || stored);
    }).catch(() => undefined);
    return () => { alive = false; };
  }, [teachExample, precedentId, precedentSender]);
  const canCheck = !busy && !nothingToDo && (!choice.claim || Boolean(claim.trim())) && (!teachEmail || Boolean(teachWhy.trim())) && (!teach || !askStance || Boolean(teachStance)) && !teachBlocked;
  const canConfirm = !busy && !settleGaveUp && preview !== null && preview.can_confirm !== false
    && (preview.outbound_messages.length === 0 || consent);

  // Fix round 1 (Minor #4): move focus with the step, instead of leaving it
  // stranded on a button that just disappeared. Skipped on first mount so it
  // never fights the Modal's own open-focus.
  useEffect(() => {
    if (skipFocusEffect.current) {
      skipFocusEffect.current = false;
      return;
    }
    if (preview) {
      (preview.outbound_messages.length ? emailLeadRef : planLeadRef).current?.focus();
    } else {
      outcomeRef.current?.focus();
    }
  }, [preview]);

  // Fix round 2 (Minor #3): the focused Confirm button disappears once the
  // settling loop gives up -- move focus to the alert explaining why, rather
  // than leaving it stranded on a removed element.
  useEffect(() => {
    if (settleGaveUp) notConfirmedRef.current?.focus();
  }, [settleGaveUp]);

  // Back to the claim field once it's enabled again, so staff can correct the reference.
  useEffect(() => {
    if (claimMissing && !busy) claimRef.current?.focus();
  }, [claimMissing, busy]);

  const correction = (): CafTaskCorrection => ({
    action: choice.action,
    ...(choice.claim ? { assigned_claim_id: claim.trim() } : {}),
    ...(finalResponse ? { has_final_response: true } : {}),
    ...(row.archived && outcome !== 'archive' ? { restore_archived: true } : {}),
    ...(!row.archived && row.completed && (outcome === 'attach' || outcome === 'staff') ? { reopen: true } : {}),
  });

  const check = async () => {
    if (!canCheck) return;
    setBusy(true);
    setProblem('');
    setClaimMissing(false);
    try {
      const text = `Fixed: ${choice.label.toLowerCase()}${choice.claim ? ` (${claim.trim()})` : ''}.`;
      let reviewId: string;
      if (lastRecorded.current && lastRecorded.current.text === text) {
        // Unchanged since our own last check (or a lost-reply replay of it): reuse outright.
        reviewId = lastRecorded.current.reviewId;
      } else {
        const currentId = reviews.current_by_decision[decisionId]?.review_id ?? null;
        const current = reviews.items.find((item) => item.review_id === currentId) ?? null;
        if (!lastRecorded.current && current && current.verdict === 'incorrect') {
          // Already Wrong before this modal opened -- the preview only needs it current and Wrong.
          // Fix round 2 (Minor #1): store the text THIS modal would itself write, not the
          // colleague's own note -- otherwise an unchanged re-check reads as "changed" and
          // needlessly supersedes their note with this modal's generated one.
          reviewId = current.review_id;
          lastRecorded.current = { reviewId, revision: current.revision, text };
        } else {
          // Supersede: a fresh key for genuinely different content, checked
          // against the revision THIS modal itself last saw (per-decision,
          // never the task-wide current_revision). Fix round 2 (Minor #2): if
          // an earlier attempt at this SAME text ended unconfirmed, reuse its
          // exact key + expected_revision -- the server's own idempotency
          // check is keyed on (actor, key) and needs an identical payload
          // (including expected_revision) to recognise a replay; minting a
          // fresh one here would risk a spurious "someone else changed this"
          // if the original write actually landed.
          const expectedRevision = lastRecorded.current
            ? lastRecorded.current.revision
            : reviews.current_by_decision[decisionId]?.revision ?? 0;
          const pending = pendingWrite.current && pendingWrite.current.text === text
            ? pendingWrite.current
            : { text, key: newIdempotencyKey('caf-review'), expectedRevision };
          pendingWrite.current = pending;
          const payload = {
            decision_id: decisionId, verdict: 'incorrect' as const, correction_text: text,
            source: 'staff_report' as const, expected_revision: pending.expectedRevision,
            idempotency_key: pending.key,
          };
          const saved = await runSave(() => recordCafDecisionReview(row.id, payload), { replay: () => recordCafDecisionReview(row.id, payload) });
          if (!saved.ok || !saved.value) {
            setProblem(saved.ok ? MSG_NOT_CONFIRMED : saved.message);
            return;
          }
          pendingWrite.current = null;
          reviewId = saved.value.review_id;
          const revision = typeof saved.value.revision === 'number' ? saved.value.revision : pending.expectedRevision + 1;
          lastRecorded.current = { reviewId, revision, text };
        }
      }
      let guidance: CafCorrectionGuidanceRequest | null = null;
      if (teachExample && precedent && precedent.sender) {
        const live = await getCafCorpusEntry(precedent.id, precedent.sender);
        guidance = {
          entry_id: live.id, sender: live.sender, expected_version: live.version || 0,
          reviewed_token: live.version ? null : live.compare_token ?? null, reason: 'Fixed from a decision',
          patch: { correct_action: choice.action, ...stancePatch, ...(live.archive_override_refs && choice.action !== 'AUTO_ARCHIVE' ? { archive_override_refs: false } : {}) },
        };
      }
      if (teachEmail) {
        try {
          const existing = await getCafCorpusEntry(row.id, senderPartition(row.email_from));
          setTeachPlan({ expected: existing.version || 0, token: existing.version ? undefined : existing.compare_token });
        } catch (err) {
          if (statusOf(err) !== 404) throw err;
          setTeachPlan({ expected: 0 });
        }
      }
      const response = await previewCafCorrection(row.id, { decision_id: decisionId, review_id: reviewId, correction: correction(), guidance });
      confirmKey.current = newIdempotencyKey('caf-confirm');
      setConsent(false);
      setPreview(response);
    } catch (err) {
      if (isClaimNotFound(err)) {
        // A plain refusal with nothing written: never a "try again" loop.
        setClaimMissing(choice.claim);
        setProblem(choice.claim ? 'No claim with that reference.' : "Couldn't check the changes. The claim linked to this email can't be found.");
        return;
      }
      const failure = classifyFailure(err);
      setProblem(failure.type === 'refused' ? failure.message : MSG_TRY_AGAIN);
    } finally {
      setBusy(false);
    }
  };

  const confirm = async () => {
    if (!canConfirm || !preview) return;
    const outbound = preview.outbound_messages.length > 0;
    const payload = { preview_id: preview.preview_id, idempotency_key: confirmKey.current, confirm_outbound: outbound };
    // Fix round 2 (Important): a requested "save as approved example" is
    // never even attempted once the fix's own outcome is unconfirmed --
    // never silently dropped.
    const notConfirmedMessage = teachEmail ? `${MSG_NOT_CONFIRMED} It wasn't saved as an approved example.` : MSG_NOT_CONFIRMED;
    // Fix round 2 (Important): confirmUntilSettled only ever rejects on the
    // FIRST call of ITS OWN invocation's failure (a later poll's failure is
    // swallowed and returned as "unsettled" instead). But runSave can invoke
    // `attempt` twice -- the original, then its replay after a lost reply --
    // and only the ORIGINAL's own refusal means the preview is genuinely
    // stale; a refusal surfacing from the REPLAY (S3: original reply lost,
    // replay's own first call then gets 410) proves nothing of the sort, so
    // `expired` must only ever reflect attempt #1.
    let attempts = 0;
    let expired = false;
    const attempt = () => {
      attempts += 1;
      const isOriginalAttempt = attempts === 1;
      return confirmUntilSettled(row.id, payload).catch((err) => {
        if (isOriginalAttempt) expired = statusOf(err) === 410 || statusOf(err) === 409;
        throw err;
      });
    };
    setBusy(true);
    setProblem('');
    const result = await runSave(attempt, { replay: attempt });
    if (!result.ok) {
      setBusy(false);
      if (result.message === MSG_NOT_CONFIRMED) {
        // S3/S4: a lost original reply whose replay is itself refused or also
        // lost -- the fix may still finish server-side. Keep the check screen
        // (never back to the choices) and stop offering Check/Confirm.
        setProblem(notConfirmedMessage);
        setSettleGaveUp(true);
      } else {
        setProblem(result.message);
        if (expired) setPreview(null);
      }
      return;
    }
    const described = describeCorrection(result.value);
    if (described.state === 'unsettled') {
      // The fix may still finish server-side; keep the check screen (never
      // back to the choices) and stop offering Check/Confirm -- only Close.
      setBusy(false);
      setProblem(notConfirmedMessage);
      setSettleGaveUp(true);
      return;
    }
    if (described.state === 'partial') {
      setBusy(false);
      // A requested "save as approved example" is a separate write, made
      // below only on a fully-done outcome -- a partial correction result
      // never reaches it, so say so rather than silently dropping it.
      toast.failed(teachEmail ? `${described.message} It wasn't saved as an approved example.` : described.message);
      onFixed();
      return;
    }
    let taughtIssue: { info: boolean; message: string } | null = null;
    if (teachEmail && teachPlan) {
      const teachPayload: FromTaskPayload = {
        task_id: row.id, action: choice.action, note: teachWhy.trim(), extraction_schema: null,
        ...stancePatch,
        expected_version: teachPlan.expected, ...(teachPlan.token ? { reviewed_token: teachPlan.token } : {}),
        reason: teachPlan.expected ? 'Taught again from a fixed email' : FIRST_LABEL_REASON,
      };
      const taught = await runSave(() => recordCorpusFromTask(teachPayload), { finish: (entry) => finishPublication(entry) });
      if (!taught.ok) {
        taughtIssue = taught.message === MSG_NOT_CONFIRMED
          ? { info: true, message: "The fix to this email saved. We couldn't confirm the approved example saved — check Teach the AI in a minute." }
          : { info: false, message: 'Couldn\'t save the approved example. The fix to this email saved.' };
      }
    }
    setBusy(false);
    if (taughtIssue) {
      if (taughtIssue.info) toast.info(taughtIssue.message); else toast.failed(taughtIssue.message);
    } else if (choice.claim) {
      toast.saved(`Saved. Email attached to ${claim.trim()}${outcome === 'done' ? ' and marked done' : ''}.`);
    } else {
      toast.saved(outcome === 'archive' ? 'Saved. Email archived.' : 'Saved. Email marked as manual.');
    }
    onFixed();
  };

  const rows = preview
    ? [
      ...Object.keys(preview.changes).filter((key) => CHANGE_LABELS[key]).map((key) => ({
        label: CHANGE_LABELS[key].label,
        before: CHANGE_LABELS[key].show(preview.changes[key].before),
        after: CHANGE_LABELS[key].show(preview.changes[key].after),
      })),
      ...(preview.guidance_changes.length ? [{ label: 'Approved example', before: 'No change', after: 'Updated' }] : []),
      ...(teachEmail ? [{ label: 'Approved example', before: 'None', after: 'This email becomes one' }] : []),
    ]
    : [];

  return (
    <Modal title="Fix this email" size="wide" onClose={onClose} busy={busy}
      footer={preview ? (
        settleGaveUp ? (
          <button type="button" className="ui-btn ui-btn--primary ui-btn--md" onClick={onClose}>Close</button>
        ) : (
          <>
            <button type="button" className="ui-btn ui-btn--ghost ui-btn--md" disabled={busy} onClick={() => setPreview(null)}>Back</button>
            <button type="button" className="ui-btn ui-btn--primary ui-btn--md" disabled={!canConfirm} onClick={() => { void confirm(); }}>
              {busy ? 'Saving…' : 'Confirm changes'}
            </button>
          </>
        )
      ) : (
        <>
          <button type="button" className="ui-btn ui-btn--ghost ui-btn--md" disabled={busy} onClick={onClose}>Cancel</button>
          <button type="button" className="ui-btn ui-btn--primary ui-btn--md" disabled={!canCheck} onClick={() => { void check(); }}>
            {busy ? 'Checking…' : 'Check the changes'}
          </button>
        </>
      )}>
      {!preview && (
        <>
          <p className="cur-hint">Nothing changes until you check the changes and confirm.</p>
          <div className="cur-field">
            <label htmlFor="fix-do">What should have happened?</label>
            <select id="fix-do" ref={outcomeRef} className="cur-input" value={outcome} disabled={busy}
              onChange={(event) => setOutcome(event.target.value as Outcome)}>
              {OUTCOMES.map((item) => <option key={item.key} value={item.key}>{item.label}</option>)}
            </select>
            {nothingToDo && <span className="cur-hint">It&apos;s already archived.</span>}
          </div>
          {choice.claim && (
            <div className="cur-field">
              <label htmlFor="fix-claim">Which claim?</label>
              <input id="fix-claim" ref={claimRef} className="cur-input" value={claim} disabled={busy} aria-invalid={claimMissing}
                onChange={(event) => {
                  setClaim(event.target.value);
                  if (claimMissing) { setClaimMissing(false); setProblem(''); }
                }} />
              <span className="cur-hint">Filled in from the email when it quotes a claim.</span>
            </div>
          )}
          <label className="cur-check">
            <input type="checkbox" checked={finalResponse} disabled={busy} onChange={(event) => setFinalResponse(event.target.checked)} />
            <span>Mark as final response</span>
          </label>
          <label className="cur-check">
            <input type="checkbox" checked={teach} disabled={busy} onChange={(event) => setTeach(event.target.checked)} />
            <span>
              {precedent && !precedent.missing ? 'Also update the approved example, so the AI learns this' : 'Also save this email as an approved example, so the AI learns this'}
              <span className="cur-hint">Leave unticked to fix just this one email.</span>
            </span>
          </label>
          {teach && !teachBlocked && askStance && (
            <FinalResponseSelect id="fix-final" value={teachStance} disabled={busy} onChange={setTeachStance}
              hint={teachStance ? undefined : 'Choose whether this is a final response.'} />
          )}
          {teachEmail && !teachBlocked && (
            <div className="cur-field">
              <label htmlFor="fix-why">Why?</label>
              <textarea id="fix-why" className="cur-input" rows={2} maxLength={4000} value={teachWhy} disabled={busy}
                onChange={(event) => setTeachWhy(event.target.value)} />
              <span className="cur-hint">The AI reads this when it sees similar emails.</span>
            </div>
          )}
          {teachBlocked && <span className="cur-hint">An archived email can&apos;t be used as an example.</span>}
        </>
      )}
      {preview && (
        <>
          <p ref={planLeadRef} tabIndex={-1}>Check what will change:</p>
          <table className="cur-ba" aria-label="What will change">
            <thead><tr><th><span className="cur-sr">Item</span></th><th>Now</th><th>After</th></tr></thead>
            <tbody>
              {rows.map((item) => (
                <tr key={`${item.label}-${item.after}`}>
                  <td className="cur-muted">{item.label}</td><td>{item.before}</td>
                  <td className={item.before !== item.after ? 'cur-chg-now' : undefined}>{item.after}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <ConflictWarnings conflicts={preview.example_conflicts ?? []}
            proposal={{ action: choice.action, final_response_stance: teachStance }} />
          {!(preview.example_conflicts ?? []).length && preview.warnings.length > 0 && (
            <ul className="cur-hint" aria-label="Warnings">{preview.warnings.map((warning) => <li key={warning}>{warning}</li>)}</ul>
          )}
          {preview.outbound_messages.length ? (
            <div className="cur-summary-box">
              <p><b ref={emailLeadRef} tabIndex={-1}>This fix will email the client:</b></p>
              <ul>
                {preview.outbound_messages.map((message) => {
                  const to = message.to_name || message.to?.[0]?.name || 'the client';
                  const email = message.to_email || message.to?.[0]?.email;
                  return <li key={message.effect_key}>{`${to}${email ? ` (${email})` : ''}${message.subject ? ` · ${message.subject}` : ''}`}</li>;
                })}
              </ul>
              <label className="cur-check">
                <input type="checkbox" checked={consent} disabled={busy || settleGaveUp} onChange={(event) => setConsent(event.target.checked)} />
                <span>Send this email to the client</span>
              </label>
            </div>
          ) : <p className="cur-hint">Nothing is sent to the client.</p>}
          {preview.can_confirm === false && (
            <div className="cur-alert cur-alert--error" role="alert">This change can&apos;t be confirmed right now. Go back and check again.</div>
          )}
          <p className="cur-hint">This check is valid until {formatClock(preview.expires_at)}.</p>
        </>
      )}
      {problem && <div ref={notConfirmedRef} tabIndex={-1} className="cur-alert cur-alert--error" role="alert">{problem}</div>}
    </Modal>
  );
};
